import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, FolderOpen, Edit, Trash2, Eye, Users, Filter, BarChart3, LayoutGrid, Table, Maximize, Minimize, Building2, DollarSign } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchPrograms,
  createProgram,
  updateProgram,
  deleteProgram,
  setFilters,
  clearError,
  fetchProgramAnalytics
} from '@/store/slices/programsSlice';
import { fetchTeams } from '@/store/slices/teamsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassSelect,
  GlassPageHeader,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
  MetricCard,
  CollapsibleMetricsSection,
} from '@/design-system';
import ProgramForm from './components/ProgramForm';
import TeamManagement from './components/TeamManagement';
import ProgramAnalyticsModal from './components/ProgramAnalyticsModal';
import ProgramBudgetModal from './components/ProgramBudgetModal';
import ProgramDetailModal from './components/ProgramDetailModal';
import type { Program, Column, AppUser } from '@/types';
import { api } from '@/utils/api';

const ProgramsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    programs,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isLoadingAnalytics,
    analytics,
    error,
    filters
  } = useSelector((state: RootState) => state.programs);

  const { teams } = useSelector((state: RootState) => state.teams);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [showFilters, setShowFilters] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [users, setUsers] = useState<AppUser[]>([]);

  useEffect(() => {
    dispatch(fetchPrograms(filters));
    dispatch(fetchTeams());
  }, [dispatch, filters]);

  useEffect(() => {
    const loadLeads = async () => {
      try {
        const response = await api.get('/user-scopes/suggestions?scope=PROGRAM_LEAD_SCOPE');
        if (response.data.success) {
          const suggestions = response.data.data.suggestions || [];
          const userSuggestions = suggestions
            .filter((s: any) => s.type === 'USER')
            .map((s: any) => ({
              id: s.id,
              firstName: s.label.split(' ')[0],
              lastName: s.label.split(' ').slice(1).join(' '),
            }));
          setUsers(userSuggestions);
        }
      } catch {
        // Silently fail - filter dropdown just won't have lead options
      }
    };
    loadLeads();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreateProgram = async (data: Partial<Program>) => {
    const result = await dispatch(createProgram(data));
    if (createProgram.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateProgram = async (data: Partial<Program>) => {
    if (!selectedProgram) return;
    const result = await dispatch(updateProgram({ id: selectedProgram.id, data }));
    if (updateProgram.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedProgram(null);
    }
  };

  const handleDeleteProgram = async () => {
    if (!selectedProgram) return;
    const result = await dispatch(deleteProgram(selectedProgram.id));
    if (deleteProgram.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedProgram(null);
    }
  };

  const handleShowAnalytics = async (program: Program) => {
    setSelectedProgram(program);
    setIsAnalyticsModalOpen(true);
    dispatch(fetchProgramAnalytics(program.id));
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    dispatch(setFilters(newFilters));
  };

  const statusOptions = [
    { value: '', label: 'All Programs' },
    { value: 'true', label: 'Active Only' },
    { value: 'false', label: 'Inactive Only' }
  ];

  const leadOptions = [
    { value: '', label: 'All Leads' },
    ...users.map(user => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`
    }))
  ];

  // Calculate metrics
  const totalTeams = programs.reduce((sum, p) => sum + (p._count?.teams || 0), 0);
  const totalPrimaryEpics = programs.reduce((sum, p) => sum + (p._count?.primaryEpics || p._count?.epics || 0), 0);
  const totalOtherEpics = programs.reduce((sum, p) => sum + (p._count?.otherEpics || 0), 0);
  const totalContributingIS = programs.reduce((sum, p) => sum + (p._count?.contributingIS || 0), 0);

  // Table columns for DataTable view
  const columns: Column<Program>[] = [
    {
      key: 'name',
      header: 'Program Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-[var(--text)] text-[13px]">{value}</div>
          {row.description && (
            <div className="text-xs text-[var(--muted)] truncate max-w-xs">
              {row.description}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'category',
      header: 'Category',
      sortable: false,
      render: () => (
        <GlassBadge variant="info">
          Tech
        </GlassBadge>
      )
    },
    {
      key: 'lead',
      header: 'Program Lead',
      render: (value, row) => {
        if (row.leadType === 'USER' && value) {
          return `${value.firstName} ${value.lastName}`;
        } else if (row.leadType === 'TEXT' && row.leadText) {
          return row.leadText;
        }
        return 'Unassigned';
      }
    },
    {
      key: '_count',
      header: 'Teams',
      render: (value) => (
        <div className="flex items-center gap-1">
          <Users className="w-4 h-4 text-[var(--muted)]" />
          <span>{value?.teams || 0}</span>
        </div>
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (value) => (
        <GlassBadge variant={value ? 'success' : 'default'}>
          {value ? 'Active' : 'Inactive'}
        </GlassBadge>
      )
    }
  ];

  const tableActions = [
    {
      label: 'View',
      icon: Eye,
      onClick: (program: Program) => {
        setSelectedProgram(program);
        setIsViewModalOpen(true);
      }
    },
    {
      label: 'Manage Teams',
      icon: Users,
      onClick: (program: Program) => {
        setSelectedProgram(program);
        setIsTeamModalOpen(true);
      }
    },
    {
      label: 'Budget & Costs',
      icon: DollarSign,
      onClick: (program: Program) => {
        setSelectedProgram(program);
        setIsBudgetModalOpen(true);
      }
    },
    {
      label: 'Edit',
      icon: Edit,
      onClick: (program: Program) => {
        setSelectedProgram(program);
        setIsEditModalOpen(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (program: Program) => {
        setSelectedProgram(program);
        setIsDeleteModalOpen(true);
      }
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading programs..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      {!isMaximized && (
        <>
          <SectionReveal>
            <GlassPageHeader
              title="Programs"
              subtitle="Manage delivery programs and capacity"
              icon={<Building2 className="w-6 h-6" />}
              iconGradient="cyan"
              className="mb-6"
              actions={
                <div className="flex items-center gap-3">
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowFilters(true)}
                    leftIcon={<Filter className="w-4 h-4" />}
                  >
                    Filters
                  </GlassButton>
                  <GlassButton
                    variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    leftIcon={<LayoutGrid className="w-4 h-4" />}
                  >
                    Grid
                  </GlassButton>
                  <GlassButton
                    variant={viewMode === 'table' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    leftIcon={<Table className="w-4 h-4" />}
                  >
                    Table
                  </GlassButton>
                  {viewMode === 'table' && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => setIsMaximized(true)}
                      leftIcon={<Maximize className="w-4 h-4" />}
                    >
                      Maximize
                    </GlassButton>
                  )}
                </div>
              }
            />
          </SectionReveal>

          {/* Metrics Row */}
          <SectionReveal delay={0.1}>
            <CollapsibleMetricsSection storageKey="programs">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  title="Total Teams"
                  value={totalTeams}
                  icon={<Users className="w-5 h-5" />}
                  iconColor="cyan"
                />
                <MetricCard
                  title="Primary EPICs"
                  value={totalPrimaryEpics}
                  icon={<BarChart3 className="w-5 h-5" />}
                  iconColor="blue"
                />
                <MetricCard
                  title="Other EPICs"
                  value={totalOtherEpics}
                  subtitle="Via allocations"
                  icon={<FolderOpen className="w-5 h-5" />}
                  iconColor="violet"
                />
                <MetricCard
                  title="Contributing to IS"
                  value={totalContributingIS}
                  subtitle="Unique IS count"
                  icon={<Building2 className="w-5 h-5" />}
                  iconColor="emerald"
                />
              </div>
            </CollapsibleMetricsSection>
          </SectionReveal>
        </>
      )}

      {!isMaximized && error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {/* Filters Drawer */}
      {!isMaximized && (
        <div
          className={`fixed inset-0 z-40 transition ${showFilters ? 'pointer-events-auto' : 'pointer-events-none'}`}
          aria-hidden={!showFilters}
        >
          <div
            className={`absolute inset-0 bg-black/25 backdrop-blur-sm transition-opacity ${showFilters ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => setShowFilters(false)}
          />
          <div
            className={`absolute right-0 top-0 h-full w-80 glass-strong border-l border-white/20 shadow-xl transition-transform duration-300 ${
              showFilters ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <h4 className="text-sm font-semibold text-[var(--text)]">Filters</h4>
                <p className="text-xs text-[var(--muted)]">Refine Programs</p>
              </div>
              <GlassButton variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                Close
              </GlassButton>
            </div>
            <div className="p-5 space-y-5">
              <div className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">Status</span>
                <GlassSelect
                  value={filters.isActive?.toString() || ''}
                  onChange={(value) => handleFilterChange('isActive', value)}
                  options={statusOptions}
                  placeholder="Status"
                  fullWidth
                />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">Program Lead</span>
                <GlassSelect
                  value={filters.leadId || ''}
                  onChange={(value) => handleFilterChange('leadId', value)}
                  options={leadOptions}
                  placeholder="Program Lead"
                  fullWidth
                />
              </div>
              <div className="flex gap-2 pt-2">
                <GlassButton
                  variant="ghost"
                  onClick={() => dispatch(setFilters({}))}
                  leftIcon={<Filter className="w-4 h-4" />}
                  fullWidth
                >
                  Clear Filters
                </GlassButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!isMaximized && viewMode === 'grid' ? (
        /* Grid View */
        <SectionReveal delay={0.2}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <FolderOpen className="w-12 h-12 text-[var(--muted)] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[var(--muted)] mb-2">No Programs</h3>
                <p className="text-[var(--muted)]/70">Create your first program to get started</p>
              </div>
            ) : (
              programs.map((program) => (
                <GlassCard key={program.id} hover className="flex flex-col">
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[var(--text)]">{program.name}</h3>
                        {program.description && (
                          <p className="text-sm text-[var(--muted)] mt-1 line-clamp-2">
                            {program.description}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 ml-3">
                        <GlassBadge variant="info" size="sm">Tech</GlassBadge>
                        <GlassBadge variant={program.isActive ? 'success' : 'default'} size="sm">
                          {program.isActive ? 'Active' : 'Inactive'}
                        </GlassBadge>
                      </div>
                    </div>

                    <div className="space-y-3 mt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Program Lead:</span>
                        <span className="text-[var(--text)] font-medium">
                          {program.leadType === 'USER' && program.lead
                            ? `${program.lead.firstName} ${program.lead.lastName}`
                            : program.leadType === 'TEXT' && program.leadText
                            ? program.leadText
                            : 'Unassigned'
                          }
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Teams:</span>
                        <span className="text-accent-blue font-semibold flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {program._count?.teams || 0}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted)]">Primary EPICs:</span>
                        <span className="text-accent-violet font-semibold">
                          {program._count?.primaryEpics || 0}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-[var(--border)]">
                      <div className="flex items-center gap-1">
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedProgram(program);
                            setIsTeamModalOpen(true);
                          }}
                          title="Manage Teams"
                        >
                          <Users className="w-4 h-4" />
                        </GlassButton>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedProgram(program);
                            setIsBudgetModalOpen(true);
                          }}
                          title="Budget & Costs"
                        >
                          <DollarSign className="w-4 h-4" />
                        </GlassButton>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedProgram(program);
                            setIsEditModalOpen(true);
                          }}
                          title="Edit Program"
                        >
                          <Edit className="w-4 h-4" />
                        </GlassButton>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedProgram(program);
                            setIsDeleteModalOpen(true);
                          }}
                          title="Delete Program"
                        >
                          <Trash2 className="w-4 h-4" />
                        </GlassButton>
                      </div>

                      <GlassButton
                        variant="secondary"
                        size="sm"
                        leftIcon={<BarChart3 className="w-4 h-4" />}
                        onClick={() => handleShowAnalytics(program)}
                      >
                        Analytics
                      </GlassButton>
                    </div>
                  </div>
                </GlassCard>
              ))
            )}
          </div>
        </SectionReveal>
      ) : null}

      {/* Maximized View - Outside SectionReveal to avoid stacking context */}
      {isMaximized && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
            onClick={() => setIsMaximized(false)}
          />
          <GlassCard
            variant="strong"
            className="fixed inset-4 z-[60] flex flex-col overflow-hidden"
            padding="none"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text)]">Programs</h3>
                <p className="text-sm text-[var(--muted)]">{programs.length} programs</p>
              </div>
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => setIsMaximized(false)}
                leftIcon={<Minimize className="w-4 h-4" />}
              >
                Minimize
              </GlassButton>
            </div>
            <div className="flex-1 overflow-auto">
              <DataTable
                data={programs}
                columns={columns}
                actions={tableActions}
                searchable
                sortable
                pagination
                pageSize={50}
                emptyMessage="No programs found"
              />
            </div>
          </GlassCard>
        </>
      )}

      {/* Normal Table View - Inside SectionReveal with scroll container */}
      {!isMaximized && viewMode === 'table' && (
        <SectionReveal delay={0.2}>
          <GlassCard variant="default" padding="none">
            <div className="max-h-[calc(100vh-350px)] overflow-auto">
              <DataTable
                data={programs}
                columns={columns}
                actions={tableActions}
                searchable
                sortable
                pagination
                pageSize={15}
                emptyMessage="No programs found"
              />
            </div>
          </GlassCard>
        </SectionReveal>
      )}

      {/* Floating New Program button */}
      {!isMaximized && (
        <button
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-cyan-500 to-cyan-600 text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
          onClick={() => setIsCreateModalOpen(true)}
          aria-label="Create Program"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Program"
        subtitle="Add a new delivery program"
        icon={<FolderOpen className="w-5 h-5" />}
        iconGradient="cyan"
        size="lg"
      >
        <ProgramForm
          onSubmit={handleCreateProgram}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isCreating}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProgram(null);
        }}
        title="Edit Program"
        subtitle={selectedProgram?.name}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="lg"
      >
        <ProgramForm
          initialData={selectedProgram}
          onSubmit={handleUpdateProgram}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedProgram(null);
          }}
          isLoading={isUpdating}
        />
      </GlassModal>

      {/* Team Management Modal */}
      <GlassModal
        isOpen={isTeamModalOpen}
        onClose={() => {
          setIsTeamModalOpen(false);
          setSelectedProgram(null);
        }}
        title="Manage Teams"
        subtitle={selectedProgram?.name}
        icon={<Users className="w-5 h-5" />}
        iconGradient="emerald"
        size="xl"
      >
        {selectedProgram && (
          <TeamManagement
            program={selectedProgram}
            teams={teams.filter(t => t.programId === selectedProgram.id)}
            onClose={() => {
              setIsTeamModalOpen(false);
              setSelectedProgram(null);
            }}
          />
        )}
      </GlassModal>

      {/* Delete Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedProgram(null);
        }}
        onConfirm={handleDeleteProgram}
        title="Delete Program"
        message={`Are you sure you want to delete "${selectedProgram?.name}"? This will also delete all associated teams and cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Analytics Modal */}
      <ProgramAnalyticsModal
        isOpen={isAnalyticsModalOpen}
        onClose={() => {
          setIsAnalyticsModalOpen(false);
          setSelectedProgram(null);
        }}
        analytics={analytics}
        isLoading={isLoadingAnalytics}
      />

      {/* Budget Modal */}
      <ProgramBudgetModal
        isOpen={isBudgetModalOpen}
        onClose={() => {
          setIsBudgetModalOpen(false);
          setSelectedProgram(null);
        }}
        program={selectedProgram}
      />

      {/* View Detail Modal */}
      <ProgramDetailModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedProgram(null);
        }}
        program={selectedProgram}
        onEdit={() => {
          setIsViewModalOpen(false);
          setIsEditModalOpen(true);
        }}
      />
    </div>
  );
};

export default ProgramsPage;
